import {
  highIncomeCountries,
  lowIncomeCountries,
  lowerMiddleIncomeCountries,
  upperMidIncomeCountries,
} from '@/assets/messages';
import { AnnotatedType, PrimaryAnnotatedType } from '@/components/annotated-type';
import { HorizontalBarChart } from '@/components/bar-chart/horizontal';
import { BarChartItem } from '@/components/bar-chart/vertical';
import { ScrollGraphic, ScrollRoot } from '@/components/microstory/scroll';
import { CenteredStoryText } from '@/components/microstory/text';
import { useNumberFormatting } from '@/utils/use-number-formatting';
import { I18n } from '@lingui/core';
import { Trans, t } from '@lingui/macro';
import { useLingui } from '@lingui/react';
import { useMemo } from 'react';

type IncomeCountry = keyof WhereYouCallHomeData['younger_identify_with_world'];

function getLabel(i18n: I18n, key: IncomeCountry) {
  switch (key) {
    case 'hic':
      return highIncomeCountries(i18n);
    case 'lic':
      return lowIncomeCountries(i18n);
    case 'umc':
      return upperMidIncomeCountries(i18n);
    case 'lmc':
      return lowerMiddleIncomeCountries(i18n);
  }
}

export function WhereYouCallHome2023Chapter3({
  data,
}: {
  data: WhereYouCallHomeData['younger_identify_with_world'];
}) {
  const { i18n } = useLingui();
  const formatPercentage = useNumberFormatting('percent');

  const chartData: BarChartItem[] = useMemo(
    () =>
      Object.entries(data).map(([key, value]) => ({
        label: getLabel(i18n, key as IncomeCountry),
        value,
      })),
    [data, i18n]
  );

  return (
    <>
      <CenteredStoryText>
        <Trans id="where-you-call-home-2023.chapter-3.text-1">
          But views differ across countries—and country income levels…
        </Trans>
      </CenteredStoryText>

      <ScrollRoot>
        <ScrollGraphic>
          <HorizontalBarChart
            label={t({
              id: 'where-you-call-home-2023.chapter-3.chart-label',
              message: '% of young people who identify most with the world in:',
            })}
            data={chartData}
            formatValue={formatPercentage}
          />
        </ScrollGraphic>
      </ScrollRoot>

      <CenteredStoryText>
        <Trans id="where-you-call-home-2023.chapter-3.text-2">
          What does it mean to identify most with the world? Those who do may have a more{' '}
          <PrimaryAnnotatedType>global mindset</PrimaryAnnotatedType>
          and be <AnnotatedType>less prone to nationalistic thinking</AnnotatedType>.
        </Trans>
      </CenteredStoryText>

      <CenteredStoryText>
        <Trans id="where-you-call-home-2023.chapter-3.text-3">
          For example, people who say they would like to move to another country are nearly{' '}
          <AnnotatedType>twice as likely to identify as a global citizen</AnnotatedType> compared
          with those who wouldn’t.
        </Trans>
      </CenteredStoryText>
    </>
  );
}
